import BreadCrumb from "Components/Common/BreadCrumb";
import {
  superadminGetCompanySettingById,
  superadminPostCompanySettingById,
} from "helpers/backend_helpers";
import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Label,
  Row,
  Spinner,
  Alert,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";

interface CompanySetting {
  allowCheckPointPatrol: boolean;
}

const CheckPointPatrol = () => {
  const query = useLocation();
  const params = useParams();
  const [data, setData] = useState<CompanySetting>({
    allowCheckPointPatrol: false,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  const getData = async () => {
    setLoading(true);
    try {
      const response: any = await superadminGetCompanySettingById(params.id);
      setData(response);
    } catch (error) {
      console.error("Error loading company setting:", error);
      toast.error("Gagal memuat data konfigurasi");
    } finally {
      setLoading(false);
    }
  };

  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      allowCheckPointPatrol: data.allowCheckPointPatrol || false,
    },
    validationSchema: Yup.object({
      allowCheckPointPatrol: Yup.boolean(),
    }),
    onSubmit: async (values) => {
      if (saving) return;

      setSaving(true);
      try {
        await superadminPostCompanySettingById({
          ...values,
          companyId: params.id,
        });
        await getData();
        toast.success(
          <div className="d-flex align-items-center">
            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
            Konfigurasi berhasil diperbarui!
          </div>
        );
      } catch (error) {
        toast.error(
          <div className="d-flex align-items-center">
            <i className="ri-error-warning-line me-2 fs-5"></i>
            Gagal memperbarui konfigurasi
          </div>
        );
      } finally {
        setSaving(false);
      }
    },
  });

  useEffect(() => {
    getData();
  }, []);

  document.title = `Konfigurasi Cek Poin - ${query.state || "Perusahaan"}`;

  const hasChanges =
    validation.values.allowCheckPointPatrol !==
    (data.allowCheckPointPatrol || false);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Konfigurasi Cek Poin"
            pageTitle="Data Perusahaan"
          />

          <Row>
            <Col lg={8} xl={12} className="mx-auto">
              <Card className="shadow-sm border-0 overflow-hidden">
                <CardHeader className="bg-gradient-primary bg-soft border-0">
                  <div className="d-flex align-items-start gap-3">
                    <div className="avatar-sm flex-shrink-0">
                      <div className="avatar-title bg-primary-subtle text-primary rounded-circle fs-20">
                        <i className="ri-map-pin-line"></i>
                      </div>
                    </div>
                    <div className="flex-grow-1">
                      <h5 className="card-title mb-1 text-dark">
                        Pengaturan Cek Poin Patrol
                      </h5>
                      <p className="text-muted mb-0 small">
                        {query.state || "Perusahaan"}
                      </p>
                    </div>
                  </div>
                </CardHeader>

                <CardBody className="p-4">
                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" size="lg" />
                      <p className="text-muted mt-3 mb-0">
                        Memuat konfigurasi...
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={validation.handleSubmit}>
                      {/* Info Alert */}
                      <Alert color="info" className="border-0 mb-4">
                        <div className="d-flex align-items-start">
                          <i className="ri-information-line me-2 fs-18"></i>
                          <div>
                            <strong className="d-block mb-1">
                              Tentang Fitur Ini
                            </strong>
                            <small>
                              Aktifkan untuk mengizinkan petugas melakukan
                              patrol dengan sistem checkpoint di lokasi yang
                              telah ditentukan.
                            </small>
                          </div>
                        </div>
                      </Alert>

                      {/* Switch Toggle - Modern Style */}
                      <div className="border rounded-3 p-4 bg-light bg-opacity-50">
                        <div className="d-flex align-items-center justify-content-between">
                          <div>
                            <Label className="form-label mb-1 fw-semibold text-dark">
                              Status Cek Poin Patrol
                            </Label>
                            <p className="text-muted small mb-0">
                              {validation.values.allowCheckPointPatrol
                                ? "Fitur checkpoint patrol aktif"
                                : "Fitur checkpoint patrol nonaktif"}
                            </p>
                          </div>

                          <div className="form-check form-switch form-switch-lg form-switch-success">
                            <Input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              id="allowCheckPointPatrol"
                              checked={validation.values.allowCheckPointPatrol}
                              onChange={(e) => {
                                validation.setFieldValue(
                                  "allowCheckPointPatrol",
                                  e.target.checked
                                );
                              }}
                              disabled={saving}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Status Badge */}
                      <div className="mt-3 text-end">
                        <span
                          className={`badge ${
                            validation.values.allowCheckPointPatrol
                              ? "badge-soft-success"
                              : "badge-soft-secondary"
                          } fs-12`}
                        >
                          <i
                            className={`ri-${
                              validation.values.allowCheckPointPatrol
                                ? "checkbox-circle"
                                : "close-circle"
                            }-line align-middle me-1`}
                          ></i>
                          {validation.values.allowCheckPointPatrol
                            ? "Aktif"
                            : "Nonaktif"}
                        </span>
                      </div>

                      {hasChanges && (
                        <Alert color="warning" className="border-0 mt-4 mb-0">
                          <div className="d-flex align-items-center">
                            <i className="ri-alert-line me-2 fs-18"></i>
                            <small>
                              Anda memiliki perubahan yang belum disimpan
                            </small>
                          </div>
                        </Alert>
                      )}

                      <div className="d-flex gap-2 justify-content-end pt-4 border-top mt-4">
                        <Button
                          size="sm"
                          type="button"
                          color="light"
                          className="btn-border"
                          onClick={() => {
                            validation.resetForm();
                            getData();
                          }}
                          disabled={saving || loading || !hasChanges}
                        >
                          <i className="ri-refresh-line me-1 align-middle"></i>
                          Batalkan
                        </Button>
                        <Button
                          size="sm"
                          type="submit"
                          color="primary"
                          className="btn-label"
                          disabled={saving || loading || !hasChanges}
                        >
                          {saving ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              Menyimpan...
                            </>
                          ) : (
                            <>
                              <i className="ri-save-line label-icon align-middle fs-16 me-2"></i>
                              Simpan Perubahan
                            </>
                          )}
                        </Button>
                      </div>
                    </form>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default CheckPointPatrol;
